import {
  Badge,
  Card,
  ChannelIcon,
  IconButton,
  Label,
  Text,
} from '@buffer-mono/popcorn'

import { useConnectChannel } from '~publish/hooks/useConnectChannel'
import { useCta } from '~publish/hooks/useCta'
import { Service } from '~publish/legacy/constants/services/ServiceDefinitions'
import {
  COMING_SOON_SERVICES,
  isComingSoonService,
} from '~publish/pages/Community/util/comingSoonServices'

import styles from './ConnectSupportedChannelsCard.module.css'

import type { JSX } from 'react'

export type ChannelOption = {
  service: Service
  label: string
}

const commentsChannelOptions: ChannelOption[] = [
  { service: Service.Threads, label: 'Threads' },
  { service: Service.Facebook, label: 'Facebook' },
  { service: Service.Instagram, label: 'Instagram' },
  { service: Service.Linkedin, label: 'LinkedIn' },
  { service: Service.Googlebusiness, label: 'Google Business' },
  { service: Service.Mastodon, label: 'Mastodon' },
  { service: Service.Tiktok, label: 'TikTok' },
  { service: Service.Youtube, label: 'YouTube' },
]

type ConnectSupportedChannelsCardProps = {
  hint: string
  /**
   * Channels to offer, in display order. Defaults to the services Community
   * supports for comments.
   */
  options?: ChannelOption[]
  /**
   * Services shown with a "Coming soon" badge. Their tile is disabled: this
   * surface can't show their engagements yet, so it offers no connect action.
   * Defaults to the Community-wide coming-soon list.
   */
  comingSoonServices?: readonly Service[]
  /**
   * CTA the connect click is attributed to. Each surface passes its own so
   * connects aren't all counted against whichever one happened to be first.
   */
  cta?: CTA
}

export function ConnectSupportedChannelsCard({
  hint,
  options = commentsChannelOptions,
  comingSoonServices = COMING_SOON_SERVICES,
  cta = 'publish-comments-emptyState-connectChannel-1',
}: ConnectSupportedChannelsCardProps): JSX.Element {
  const connectChannel = useConnectChannel({ cta })
  const { clicked: ctaClicked } = useCta(cta, {
    upgradePathName: 'addChannel-upgrade',
  })

  const handleConnect = (service: Service): void => {
    ctaClicked()
    connectChannel(service)
  }

  return (
    <Card padding="lg" className={styles.card}>
      <Text size="sm" color="subtle" className={styles.cardHint}>
        {hint}
      </Text>
      <div className={styles.channelsGrid}>
        {options.map((option) =>
          isComingSoonService(option.service, comingSoonServices) ? (
            <ComingSoonChannelItem key={option.service} option={option} />
          ) : (
            <SupportedChannelItem
              key={option.service}
              option={option}
              onConnect={handleConnect}
            />
          ),
        )}
      </div>
    </Card>
  )
}

type ChannelItemProps = {
  option: ChannelOption
  onConnect: (service: Service) => void
}

function SupportedChannelItem({
  option,
  onConnect,
}: ChannelItemProps): JSX.Element {
  return (
    <div className={styles.channelItem}>
      <IconButton
        variant="tertiary"
        size="large"
        label={`Connect ${option.label}`}
        onClick={() => onConnect(option.service)}
        className={styles.iconWrapper}
      >
        <ChannelIcon
          type={option.service}
          color="subtle"
          className={styles.channelIcon}
        />
      </IconButton>
      <Label>{option.label}</Label>
    </div>
  )
}

/**
 * A channel whose engagements this surface can't show yet. The tile names the
 * channel but stays disabled: a connect action here would leave the user with
 * a connected channel and an empty surface.
 */
function ComingSoonChannelItem({
  option,
}: {
  option: ChannelOption
}): JSX.Element {
  return (
    <div className={styles.channelItem}>
      <IconButton
        variant="tertiary"
        size="large"
        label={`${option.label} coming soon`}
        disabled
        className={styles.iconWrapper}
      >
        <ChannelIcon
          type={option.service}
          color="subtle"
          className={styles.channelIcon}
        />
      </IconButton>
      <Label className={styles.comingSoonLabel}>{option.label}</Label>
      <Badge size="small" variant="neutral">
        Coming soon
      </Badge>
    </div>
  )
}
